import * as React from "react";
import { z } from "zod";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { useToast } from "@/components/ui/toast";
import { Section } from "@/components/layout/section";
import { container } from "@/lib/supabase/di/container";

const schema = z.object({
  fullName: z.string().min(2, "Nama lengkap wajib diisi."),
  email: z.string().email("Format email tidak valid."),
  phone: z.string().optional(),
  message: z.string().optional(),
});

type FormState = {
  fullName: string;
  email: string;
  phone: string;
  message: string;
};

const EMPTY: FormState = { fullName: "", email: "", phone: "", message: "" };

export function WaitlistForm() {
  const { toast } = useToast();
  const [form, setForm] = React.useState<FormState>(EMPTY);
  const [errors, setErrors] = React.useState<Partial<Record<keyof FormState, string>>>({});
  const [pending, setPending] = React.useState(false);

  function update<K extends keyof FormState>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const result = schema.safeParse(form);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof FormState, string>> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof FormState;
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    setPending(true);
    try {
      await container.leadService.submitLead({ ...result.data, source: "landing" });
      toast("Terima kasih! Kami akan menghubungi Anda segera.", "success");
      setForm(EMPTY);
    } catch (err) {
      toast(err instanceof Error ? err.message : "Gagal mengirim. Silakan coba lagi.", "error");
    } finally {
      setPending(false);
    }
  }

  return (
    <Section id="kontak">
      <div className="mx-auto max-w-xl">
        <div className="text-center">
          <h2 className="text-3xl font-bold sm:text-4xl">Daftar Akses Awal</h2>
          <p className="mt-4 text-text-muted">
            Jadilah bagian dari komunitas keamanan malam pertama. Daftarkan diri Anda sekarang.
          </p>
        </div>

        <Card className="mt-8">
          <form onSubmit={onSubmit} noValidate className="space-y-4">
            <div>
              <Label htmlFor="fullName">Nama Lengkap</Label>
              <Input
                id="fullName"
                className="mt-1.5"
                value={form.fullName}
                onChange={(e) => update("fullName", e.target.value)}
                aria-invalid={Boolean(errors.fullName)}
                aria-describedby={errors.fullName ? "fullName-error" : undefined}
                placeholder="Budi Santoso"
              />
              {errors.fullName && (
                <p id="fullName-error" role="alert" className="mt-1.5 text-sm text-danger">
                  {errors.fullName}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                className="mt-1.5"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? "email-error" : undefined}
                placeholder="budi@example.com"
              />
              {errors.email && (
                <p id="email-error" role="alert" className="mt-1.5 text-sm text-danger">
                  {errors.email}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="phone">Nomor Telepon (opsional)</Label>
              <Input
                id="phone"
                type="tel"
                className="mt-1.5"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="0812-3456-7890"
              />
            </div>

            <div>
              <Label htmlFor="message">Pesan (opsional)</Label>
              <Textarea
                id="message"
                className="mt-1.5"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="Ceritakan lingkungan yang ingin Anda amankan..."
              />
            </div>

            <Button type="submit" size="lg" className="w-full" disabled={pending}>
              {pending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Mengirim...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Daftar Sekarang
                </>
              )}
            </Button>
          </form>
        </Card>
      </div>
    </Section>
  );
}
